import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { CollectionPropertyType, ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { useBlockGroupContext } from "components/form/Blocks";
import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useSectionTwoImagesFiveTextsApi from "hooks/api/private/blocks/useSectionTwoImagesFiveTextsApi";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import {
    SectionTwoImagesFiveTextsBody,
    SectionTwoImagesFiveTextsDetails,
} from "types/api/blocks/sectionTwoImagesFiveTexts";
import { DesktopMobileForm, LanguageEnum } from "types/api/commons";
import { DesktopOrMobile } from "types/enums";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia } from "utils/validators";

export type SectionTwoImagesFiveTextsForm = {
    id: string | undefined;
    left_img_id: string | undefined;
    left_img: DesktopMobileForm;
    right_img_id: string | undefined;
    right_img: DesktopMobileForm;
    text: {
        [x in LanguageEnum]: {
            title: string;
            subtitle: string;
            text: string;
            button_label: string;
            button_url: string;
        };
    };
};

type SectionTwoImagesFiveTextsType = {
    setLeftImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    setRightImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    buttonDelete: ButtonDeleteInterface;
    formHook: FormHookType<SectionTwoImagesFiveTextsForm>;
};

function useSectionTwoImagesFiveTexts({
    fourTexts = false,
}: {
    fourTexts: boolean;
}): SectionTwoImagesFiveTextsType {
    const { blockGroupCompositionId, blockId, graphicalOrder, blockGroupId } =
        useBlockGroupContext();

    const textWithoutValidators: CollectionPropertyType = React.useMemo(
        () => ({
            type: "collection",
            of: {
                type: "object",
                properties: {
                    title: {
                        type: "simple",
                        validators: [],
                    },
                    subtitle: {
                        type: "simple",
                        validators: [],
                    },
                    text: {
                        type: "simple",
                        validators: [],
                    },
                    button_label: {
                        type: "simple",
                        validators: [],
                    },
                    button_url: {
                        type: "simple",
                        validators: [],
                    },
                },
            },
        }),
        []
    );

    const textWithValidators: CollectionPropertyType = React.useMemo(
        () => ({
            type: "collection",
            of: {
                type: "object",
                properties: {
                    title: {
                        type: "simple",
                        validators: [],
                    },
                    subtitle: {
                        type: "simple",
                        validators: [],
                    },
                    text: {
                        type: "simple",
                        validators: [],
                    },
                    button_label: {
                        type: "simple",
                        validators: [notEmptyValidator],
                    },
                    button_url: {
                        type: "simple",
                        validators: [notEmptyValidator],
                    },
                },
            },
        }),
        []
    );

    const {
        saveSectionTwoImagesFiveTexts,
        getOneSectionTwoImagesFiveTexts,
        deleteSectionTwoImagesFiveTexts,
    } = useSectionTwoImagesFiveTextsApi();

    const [schema, setSchema] = React.useState<ObjectPropertyType>({
        type: "object",
        properties: {
            id: {
                type: "simple",
                validators: [],
            },
            right_img_id: {
                type: "simple",
                validators: [],
            },
            left_img_id: {
                type: "simple",
                validators: [],
            },
            right_img: notEmptyMedia,
            left_img: notEmptyMedia,
            text: textWithoutValidators,
        },
    });

    const initialValues: SectionTwoImagesFiveTextsForm = React.useMemo(() => {
        return {
            id: undefined,
            right_img_id: undefined,
            right_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            left_img_id: undefined,
            left_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            text: {
                it: {
                    title: "",
                    subtitle: "",
                    text: "",
                    button_label: "",
                    button_url: "",
                },
                en: {
                    title: "",
                    subtitle: "",
                    text: "",
                    button_label: "",
                    button_url: "",
                },
            },
        };
    }, []);

    const formHook = useForm<SectionTwoImagesFiveTextsForm>(
        schema,
        initialValues,
        getExtraErrorsDescription
    );

    const { showAllError, setForm, isValid, form } = formHook;

    React.useEffect(() => {
        setSchema((prevSchema) => {
            const newSchema = _.cloneDeep(prevSchema);

            if (
                form.text.en.button_url ||
                form.text.en.button_label ||
                form.text.it.button_url ||
                form.text.it.button_label
            ) {
                newSchema.properties.text = textWithValidators;
            } else {
                newSchema.properties.text = textWithoutValidators;
            }

            return newSchema;
        });
    }, [
        textWithValidators,
        textWithoutValidators,
        form.text.en.button_url,
        form.text.en.button_label,
        form.text.it.button_url,
        form.text.it.button_label,
    ]);

    const { setDesktopMobileImage: setLeftImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "left_img",
    });

    const { setDesktopMobileImage: setRightImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "right_img",
    });

    const [sectionTwoImagesFiveTextsDetails, setSectionTwoImagesFiveTextsDetails] = React.useState<
        SectionTwoImagesFiveTextsDetails | undefined
    >(undefined);

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionTwoImagesFiveTexts(
            { id: blockGroupCompositionId },
            { rejectNotFound: true }
        );

        apiCall.then(setSectionTwoImagesFiveTextsDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [
        setSectionTwoImagesFiveTextsDetails,
        getOneSectionTwoImagesFiveTexts,
        blockGroupCompositionId,
    ]);

    React.useEffect(() => {
        if (sectionTwoImagesFiveTextsDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = sectionTwoImagesFiveTextsDetails.id;
                newForm.right_img_id = sectionTwoImagesFiveTextsDetails.right_img_id;
                newForm.left_img_id = sectionTwoImagesFiveTextsDetails.left_img_id;

                newForm.text.en.button_label =
                    sectionTwoImagesFiveTextsDetails.text.en.button_label;
                newForm.text.en.button_url = sectionTwoImagesFiveTextsDetails.text.en.button_url;
                newForm.text.en.text = sectionTwoImagesFiveTextsDetails.text.en.text;
                newForm.text.en.title = sectionTwoImagesFiveTextsDetails.text.en.title;

                newForm.text.it.button_label =
                    sectionTwoImagesFiveTextsDetails.text.it.button_label;
                newForm.text.it.button_url = sectionTwoImagesFiveTextsDetails.text.it.button_url;
                newForm.text.it.text = sectionTwoImagesFiveTextsDetails.text.it.text;
                newForm.text.it.title = sectionTwoImagesFiveTextsDetails.text.it.title;

                if (fourTexts) {
                    newForm.text.en.subtitle = "";
                    newForm.text.it.subtitle = "";
                } else {
                    newForm.text.en.subtitle = sectionTwoImagesFiveTextsDetails.text.en.subtitle;
                    newForm.text.it.subtitle = sectionTwoImagesFiveTextsDetails.text.it.subtitle;
                }

                newForm.right_img = {
                    id: sectionTwoImagesFiveTextsDetails.right_img.id,
                    desktop: createAttachmentType({
                        id: sectionTwoImagesFiveTextsDetails.right_img.desktop.id,
                        label: "Desktop",
                        filename: sectionTwoImagesFiveTextsDetails.right_img.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: sectionTwoImagesFiveTextsDetails.right_img.mobile.id,
                        label: "Mobile",
                        filename: sectionTwoImagesFiveTextsDetails.right_img.mobile.filename,
                        status: 2,
                    }),
                };
                newForm.left_img = {
                    id: sectionTwoImagesFiveTextsDetails.left_img.id,
                    desktop: createAttachmentType({
                        id: sectionTwoImagesFiveTextsDetails.left_img.desktop.id,
                        label: "Desktop",
                        filename: sectionTwoImagesFiveTextsDetails.left_img.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: sectionTwoImagesFiveTextsDetails.left_img.mobile.id,
                        label: "Mobile",
                        filename: sectionTwoImagesFiveTextsDetails.left_img.mobile.filename,
                        status: 2,
                    }),
                };

                return newForm;
            });
        }
    }, [setForm, sectionTwoImagesFiveTextsDetails, fourTexts]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const body: SectionTwoImagesFiveTextsBody = {
                    id: blockGroupCompositionId,
                    right_img: {
                        id: form.right_img.id,
                        desktop: converterAttachmentToFileWithPayload(form.right_img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.right_img.mobile),
                    },
                    left_img: {
                        id: form.left_img.id,
                        desktop: converterAttachmentToFileWithPayload(form.left_img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.left_img.mobile),
                    },
                    text: {
                        it: {
                            title: form.text.it.title,
                            subtitle: fourTexts ? "" : form.text.it.subtitle,
                            text: form.text.it.text,
                            button_label: form.text.it.button_label,
                            button_url: form.text.it.button_url,
                        },
                        en: {
                            title: form.text.en.title,
                            subtitle: fourTexts ? "" : form.text.en.subtitle,
                            text: form.text.en.text,
                            button_label: form.text.en.button_label,
                            button_url: form.text.en.button_url,
                        },
                    },
                };

                const [apiCall] = saveSectionTwoImagesFiveTexts(body);

                const promises: Promise<unknown>[] = [];

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveSectionTwoImagesFiveTexts,
        addSaveAction,
        showAllError,
        blockGroupCompositionId,
        isValid,
        form,
        blockGroupId,
        blockId,
        fourTexts,
        graphicalOrder,
    ]);

    return React.useMemo(
        (): SectionTwoImagesFiveTextsType => ({
            buttonDelete: deleteSectionTwoImagesFiveTexts,
            setLeftImage,
            setRightImage,
            formHook,
        }),
        [deleteSectionTwoImagesFiveTexts, setLeftImage, setRightImage, formHook]
    );
}

export default useSectionTwoImagesFiveTexts;
